// Gemeinsame Domain-Types.
// Werden von Rider-PWA, (später) Swapper-PWA, Admin-Dashboard und Backend geteilt.
/** Sortierreihenfolge (Zustieg → leicht → schwer), nützlich für UI-Filter + Liste. */
export const DIFFICULTY_ORDER = [
    'uphill',
    'easy',
    'intermediate',
    'intermediate_plus',
    'hard',
    'expert',
    'extreme',
];
/** Kurz-Label für UI (Tooltip / Badge). */
export const DIFFICULTY_LABEL = {
    uphill: 'Uphill',
    easy: 'Easy',
    intermediate: 'Intermediate',
    intermediate_plus: 'Intermediate+',
    hard: 'Hard',
    expert: 'Expert',
    extreme: 'Extreme',
    unknown: 'Unknown',
};
const DIFFICULTY_COLOR_STANDARD = {
    uphill: '#ec4899', // pink (Zustieg, kein Descent-Rating); gelb/amber war zu ähnlich zu OSM-Landstraßen — user-request Wave 14r
    easy: '#76bb40', // grün (User-Wunsch: heller, #2e7d32 war zu dunkel)
    intermediate: '#1565c0', // blau
    intermediate_plus: '#d32f2f', // rot als Fallback; UI-Swatch + Map-Linie sind blau/rot striped
    hard: '#d32f2f', // rot
    expert: '#111111', // schwarz
    extreme: '#111111', // schwarz als Fallback; UI-Swatch ist striped
    unknown: '#888888',
};
const DIFFICULTY_COLOR_CB = {
    uphill: '#ec4899', // pink — bleibt
    easy: '#26C6DA', // cyan — sehr distinct vom gruenen Karten-Hintergrund
    intermediate: '#1565c0', // blau — bleibt
    intermediate_plus: '#D55E00', // vermillion-Fallback (Stripe blau/vermillion)
    hard: '#D55E00', // vermillion (orange-rot, klar von cyan + pink unterscheidbar)
    expert: '#111111', // schwarz — bleibt
    extreme: '#111111', // schwarz Fallback (Stripe bw)
    unknown: '#888888',
};
let _activeColorMode = 'standard';
/**
 * Aktiven Farb-Modus setzen. Wird vom Frontend-Hook aufgerufen sobald sich
 * die Profile-Pref aendert. Trigger KEINEN React-Re-Render — die Caller-
 * Komponente muss den Hook halten damit sie selbst re-rendert.
 */
export function setActiveColorMode(mode) {
    _activeColorMode = mode;
}
export function getActiveColorMode() {
    return _activeColorMode;
}
/**
 * Getter-basierte Palette: Property-Lookups lesen zur Laufzeit aus der
 * aktiven Palette. Existierende Call-Sites (DIFFICULTY_COLOR[d]) bleiben
 * funktional. Object.keys / Object.entries arbeiten weiter wie bisher
 * (alle 8 Difficulty-Strings, jeweils enumerable).
 */
export const DIFFICULTY_COLOR = (() => {
    const obj = {};
    for (const key of Object.keys(DIFFICULTY_COLOR_STANDARD)) {
        Object.defineProperty(obj, key, {
            get() {
                const palette = _activeColorMode === 'cb'
                    ? DIFFICULTY_COLOR_CB
                    : DIFFICULTY_COLOR_STANDARD;
                return palette[key];
            },
            enumerable: true,
            configurable: false,
        });
    }
    return obj;
})();
/**
 * Farbe für Selection-Aura (weicher Glow um den gerade ausgewählten Trail).
 *
 * Unterscheidet sich von DIFFICULTY_COLOR nur bei `intermediate_plus`: dessen
 * DIFFICULTY_COLOR ist die rote Fallback-Farbe (weil die Map-Linie aus
 * blauer Main-Line + roter Undercoat als Stripe rendert). Die Aura soll
 * laut User-Wunsch (Wave 14w) aber BLAU sein, weil das die dominant wahr-
 * genommene Farbe der gestreiften Linie ist.
 *
 * `extreme` nutzt schwarz (#111111) — identisch zu DIFFICULTY_COLOR.extreme,
 * explizit vom User so gewollt ("bei extreme schwarz"). Alle anderen
 * Difficulties erben direkt ihre DIFFICULTY_COLOR.
 */
export function difficultyAuraColor(d) {
    if (d === 'intermediate_plus')
        return '#1565c0'; // intermediate-blau (dominante Stripe-Farbe)
    return DIFFICULTY_COLOR[d];
}
/**
 * Hinweis für extreme / intermediate_plus: In Swatches (HTML/CSS) rendern
 * wir einen 45°-Diagonal-Stripe. Für MapLibre (Line-Layer) geht das nicht
 * trivial, daher Fallback auf eine der beiden Stripe-Farben. Die Swatch-
 * Helper-Funktion liefert einen background-Value den Swatches direkt setzen
 * können.
 *
 * Wave 14ca-fix64: Stripe-Farben fuer intermediate_plus haengen vom
 * aktiven Color-Modus ab — im CB-Modus blau/vermillion statt blau/rot.
 */
export function difficultySwatchBackground(d) {
    if (d === 'extreme') {
        // 45°-Streifen, schwarz/weiß, 6px Raster — lesbar auch bei 12-16px Swatches.
        return 'repeating-linear-gradient(45deg, #111 0 3px, #fff 3px 6px)';
    }
    if (d === 'intermediate_plus') {
        // 45°-Streifen, blau (intermediate) / rot (hard) — bzw. blau/vermillion
        // im CB-Modus. Die zweite Farbe kommt direkt aus der aktiven Palette.
        const second = _activeColorMode === 'cb'
            ? DIFFICULTY_COLOR_CB.hard
            : DIFFICULTY_COLOR_STANDARD.hard;
        return `repeating-linear-gradient(45deg, #1565c0 0 3px, ${second} 3px 6px)`;
    }
    return DIFFICULTY_COLOR[d];
}
export const DEFAULT_SOC_RANGES = { full: 75, half: 50 };
export function socBucket(socPercent, r = DEFAULT_SOC_RANGES) {
    if (socPercent >= r.full)
        return 'full';
    if (socPercent >= r.half)
        return 'half';
    return 'empty';
}
